/**
 * Generador de reportes para permisos
 */

/**
 * Imprimir reporte completo de permisos
 * @param {Array} permisos - Lista de permisos
 * @param {string} titulo - Título del reporte
 * @param {Object} filtros - Filtros aplicados
 * @param {Array} modulos - Lista de módulos para referencia
 */
export const imprimirReportePermisos = (permisos, titulo, filtros = {}, modulos = []) => {
  const ventana = window.open('', '_blank');
  if (!ventana) {
    alert('Por favor, permite las ventanas emergentes para imprimir');
    return;
  }
  
  const fechaActual = new Date().toLocaleString('es-ES');
  const totalAsignaciones = permisos.reduce((sum, p) => sum + (p.cantidadRoles || 0), 0);
  const modulosUnicos = [...new Set(permisos.map(p => p.modulo_nombre))];
  
  // Función para formatear nombre de acción
  const formatearNombre = (nombre) => {
    if (!nombre) return '';
    return nombre.split('_').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
  };
  
  ventana.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>${titulo}</title>
      <meta charset="UTF-8">
      <style>
        body {
          font-family: 'Segoe UI', Arial, sans-serif;
          margin: 40px;
          color: #333;
        }
        .header {
          text-align: center;
          margin-bottom: 30px;
          border-bottom: 2px solid #4f46e5;
          padding-bottom: 20px;
        }
        .title {
          font-size: 24px;
          font-weight: bold;
          color: #4f46e5;
        }
        .subtitle {
          color: #666;
          margin-top: 10px;
        }
        .stats {
          display: flex;
          justify-content: space-between;
          margin: 20px 0;
          padding: 15px;
          background: #f3f4f6;
          border-radius: 8px;
          flex-wrap: wrap;
          gap: 15px;
        }
        .stat-card {
          text-align: center;
          flex: 1;
          min-width: 100px;
        }
        .stat-number {
          font-size: 24px;
          font-weight: bold;
          color: #4f46e5;
        }
        .stat-label {
          font-size: 12px;
          color: #666;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 20px;
        }
        th, td {
          border: 1px solid #ddd;
          padding: 10px;
          text-align: left;
        }
        th {
          background-color: #4f46e5;
          color: white;
        }
        tr:nth-child(even) {
          background-color: #f9fafb;
        }
        .footer {
          margin-top: 30px;
          text-align: center;
          font-size: 12px;
          color: #666;
          border-top: 1px solid #ddd;
          padding-top: 20px;
        }
        .badge {
          padding: 4px 8px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: bold;
          background: #e5e7eb;
          color: #374151;
        }
        .badge-roles {
          background: #10b981;
          color: white;
        }
        @media print {
          body {
            margin: 0;
            padding: 20px;
          }
          .no-print {
            display: none;
          }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="title">${titulo}</div>
        <div class="subtitle">Fecha de generación: ${fechaActual}</div>
        ${filtros.busqueda ? `<div class="subtitle">Búsqueda: "${filtros.busqueda}"</div>` : ''}
        ${filtros.modulo ? `<div class="subtitle">Módulo: ${filtros.modulo}</div>` : ''}
      </div>
      
      <div class="stats">
        <div class="stat-card">
          <div class="stat-number">${permisos.length}</div>
          <div class="stat-label">Total Permisos</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">${modulosUnicos.length}</div>
          <div class="stat-label">Módulos</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">${totalAsignaciones}</div>
          <div class="stat-label">Asignaciones a Roles</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">${permisos.filter(p => p.descripcion).length}</div>
          <div class="stat-label">Con Descripción</div>
        </div>
      </div>
      
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Permiso</th>
            <th>Módulo</th>
            <th>Descripción</th>
            <th>Roles Asignados</th>
            <th>Fecha Creación</th>
          </tr>
        </thead>
        <tbody>
          ${permisos.map(permiso => `
            <tr>
              <td>${permiso.idPermiso}</td>
              <td>
                <strong>${formatearNombre(permiso.nombre_accion)}</strong><br>
                <small style="color:#666">${permiso.nombre_accion}</small>
              </td>
              <td>${permiso.modulo_nombre || 'No especificado'}</td>
              <td>${permiso.descripcion || 'Sin descripción'}</td>
              <td>
                <span class="badge ${permiso.cantidadRoles > 0 ? 'badge-roles' : ''}">
                  ${permiso.cantidadRoles || 0} rol(es)
                </span>
              </td>
              <td>${new Date(permiso.fecha_creacion || permiso.fechaCreacion).toLocaleDateString('es-ES')}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      
      <div class="footer">
        <p>Reporte generado automáticamente por el sistema</p>
        <p>© ${new Date().getFullYear()} - Todos los derechos reservados</p>
      </div>
    </body>
    </html>
  `);
  
  ventana.document.close();
  ventana.print();
};

/**
 * Imprimir reporte simplificado de permisos
 * @param {Array} permisos - Lista de permisos
 * @param {string} titulo - Título del reporte
 */
export const imprimirReporteSimplificado = (permisos, titulo) => {
  const ventana = window.open('', '_blank');
  if (!ventana) {
    alert('Por favor, permite las ventanas emergentes para imprimir');
    return;
  }
  
  const fechaActual = new Date().toLocaleString('es-ES');
  
  const formatearNombre = (nombre) => {
    if (!nombre) return '';
    return nombre.split('_').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
  };
  
  ventana.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>${titulo}</title>
      <meta charset="UTF-8">
      <style>
        body {
          font-family: 'Segoe UI', Arial, sans-serif;
          margin: 40px;
          color: #333;
        }
        .header {
          text-align: center;
          margin-bottom: 30px;
          border-bottom: 2px solid #4f46e5;
          padding-bottom: 20px;
        }
        .title {
          font-size: 24px;
          font-weight: bold;
          color: #4f46e5;
        }
        .subtitle {
          color: #666;
          margin-top: 10px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 20px;
        }
        th, td {
          border: 1px solid #ddd;
          padding: 8px;
          text-align: left;
        }
        th {
          background-color: #4f46e5;
          color: white;
        }
        .footer {
          margin-top: 30px;
          text-align: center;
          font-size: 12px;
          color: #666;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="title">${titulo}</div>
        <div class="subtitle">Fecha: ${fechaActual}</div>
        <div class="subtitle">Total: ${permisos.length} permisos</div>
      </div>
      
      <table>
        <thead>
          <tr>
            <th>Permiso</th>
            <th>Módulo</th>
            <th>Roles Asignados</th>
          </tr>
        </thead>
        <tbody>
          ${permisos.map(permiso => `
            <tr>
              <td>${formatearNombre(permiso.nombre_accion)}</td>
              <td>${permiso.modulo_nombre || 'No especificado'}</td>
              <td>${permiso.cantidadRoles || 0}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      
      <div class="footer">
        <p>Documento generado automáticamente</p>
      </div>
    </body>
    </html>
  `);
  
  ventana.document.close();
  ventana.print();
};